import React, {useEffect, useState} from 'react';
import {Button, Dropdown, Input, message, Modal, Select, Switch, Tooltip} from 'antd';
import {DeleteOutlined, FolderOpenOutlined, MoreOutlined, PlusOutlined, SearchOutlined} from '@ant-design/icons';
import {isImg} from "../../helpers/img";
import {isValidUrl} from "../../helpers/url";
import {
  copyToClipboardAsync,
  getStoredWatermarkImageUrl,
  getStoredWatermarkText,
  saveWatermarkImageUrl,
  saveWatermarkText,
  urlSafeBase64Encode
} from '../../helpers/clipboard';


interface BatchActionsProps {
  selectedIds: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  list: any[];
  loadHistory: () => void;
  currentDir: string;
  onDirChange: (dir: string) => void;
  onKeywordChange: (keyword: string) => void;
  onSearch: (kw: string) => void;
}

const BatchActions: React.FC<BatchActionsProps> = ({
  selectedIds, setSelectedIds, list, loadHistory, currentDir, onDirChange, onKeywordChange, onSearch
}) => {
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null);
  const [addDirModalOpen, setAddDirModalOpen] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [dirLoading, setDirLoading] = useState(false);
  const [dirList, setDirList] = useState<string[]>([]);
  const [internalDir, setInternalDir] = useState(currentDir);

  const [generateGalleryModalOpen, setGenerateGalleryModalOpen] = useState(false);
  const [galleryTitle, setGalleryTitle] = useState('');
  const [galleryBannerUrl, setGalleryBannerUrl] = useState('');
  const [galleryDescription, setGalleryDescription] = useState('');
  const [galleryAnnouncement, setGalleryAnnouncement] = useState('');
  const [galleryLoading, setGalleryLoading] = useState(false);
  
  // 图集创建成功弹框
  const [gallerySuccessModalOpen, setGallerySuccessModalOpen] = useState(false);
  const [createdGalleryUrl, setCreatedGalleryUrl] = useState('');

  // 批量复制链接加载状态
  const [batchCopyLoading, setBatchCopyLoading] = useState(false);



  // 随机路径开关，持久化到本地存储
  const RANDOM_PATH_KEY = 'imgcloud_random_path_enabled';
  const [randomPathEnabled, setRandomPathEnabled] = useState(() => {
    const stored = localStorage.getItem(RANDOM_PATH_KEY);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(RANDOM_PATH_KEY, String(randomPathEnabled));
  }, [randomPathEnabled]);

  // 拉取目录列表
  const fetchDirList = async () => {
    setDirLoading(true);
    try {
      const res = await fetch('/img-dir/list');
      const json = await res.json();
      if (json.success) {
        setDirList(json.data || []);
        // 如果当前目录不存在于新列表，自动切换到第一个
        if (!json.data.includes(internalDir)) {
          const nextDir = json.data[0] || '';
          setInternalDir(nextDir);
          onDirChange(nextDir);
        }
      } else {
        message.error(json.message || '获取目录失败');
      }
    } catch (e) {
      message.error('获取目录失败');
    } finally {
      setDirLoading(false);
    }
  };

  useEffect(() => {
    fetchDirList();
    // eslint-disable-next-line
  }, []);

  // 外部 currentDir 变化时同步 internalDir
  useEffect(() => {
    setInternalDir(currentDir);
  }, [currentDir]);

  // 删除目录操作
  const handleDeleteDir = async () => {
    if (!internalDir) return;

    if (internalDir == '默认目录' || internalDir == '全部目录') {
      message.warning(`${internalDir}不能删除`);
      return;
    }

    Modal.confirm({
      title: `确定要删除目录"${internalDir}"吗？`,
      onOk: async () => {
        setDirLoading(true);
        try {
          const res = await fetch('/img-dir/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dirName: internalDir }),
          });
          const json = await res.json();
          if (!json.success) throw new Error(json.message || '删除失败');
          message.success('目录已删除');
          await fetchDirList();
        } catch (e: any) {
          message.error(e?.message || '删除失败');
        } finally {
          setDirLoading(false);
        }
      },
    });
  };

  // 并发控制函数
  const batchProcess = async (items: any[], processFn: (item: any) => Promise<any>, concurrency: number = 15) => {
    const results: any[] = [];
    const chunks = [];
    
    // 将数组分块，每块大小为并发数
    for (let i = 0; i < items.length; i += concurrency) {
      chunks.push(items.slice(i, i + concurrency));
    }
    
    // 逐块处理，每块内并发执行
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkPromises = chunk.map(processFn);
      const chunkResults = await Promise.all(chunkPromises);
      results.push(...chunkResults);
      
      // 如果不是最后一块，等待一小段时间再处理下一块
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms 间隔
      }
    }
    
    return results;
  };

  // 处理批量复制链接
  const handleBatchCopyLinks = async () => {
    setBatchCopyLoading(true);
    try {
      const selectedFiles = list.filter(item => selectedIds.includes(item.id));
      const urls = selectedFiles.map(item => item.url).filter(Boolean);
      
      if (urls.length === 0) {
        message.warning('没有可用的链接');
        return;
      }

      // 使用并发控制批量调用接口生成短链接
      const shortUrls = await batchProcess(urls, async (url) => {
        try {
          const res = await fetch(`/u/save?url=${encodeURIComponent(url)}`, {
            method: 'POST',
          });
          const json = await res.json();
          if (json.shortId) {
            return `https://z.wiki/u/${json.shortId}`;
          } else {
            throw new Error('生成短链接失败');
          }
        } catch (error) {
          console.error('生成短链接失败:', url, error);
          return url; // 如果失败，返回原链接
        }
      }, 15); // 限制并发数为15

      const resultText = shortUrls.join('\n');
      
      await copyToClipboardAsync(resultText, `已复制 ${shortUrls.length} 个短链接`);
    } catch (error) {
      console.error('批量复制链接失败:', error);
      message.error('批量复制链接失败');
    } finally {
      setBatchCopyLoading(false);
    }
  };

  // 处理生成图集
  const handleGenerateGallery = async () => {
      if (!isAllImage) {
        message.warning('只能选中图片文件才能生成图集');
        return;
      }
      if (!galleryTitle.trim()) {
          message.warning('请输入图集标题');
          return;
      }
      
      // 校验Banner URL格式
      if (galleryBannerUrl.trim() && !isValidUrl(galleryBannerUrl.trim())) {
          message.warning('请输入有效的Banner图片URL');
          return;
      }

      setGalleryLoading(true);
      const names = selectedIds.map(id => {
        const file = list.find(item => item.id === id);
        return file ? file.url : null;
      }).filter(Boolean);

      // NOTE: The backend API now supports title, imgs, bannerUrl, description, and announcement.
      let res, json;
      try {
        res = await fetch('/gallery/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            title: galleryTitle.trim(), 
            imgs: names, 
            bannerUrl: galleryBannerUrl, 
            description: galleryDescription,
            announcement: galleryAnnouncement
          }),
        });
      } catch (e) {
        message.error('网络异常，创建图集失败');
        setGalleryLoading(false);
        return;
      }

      if (!res.ok) {
        // 尝试解析 message 字段
        try {
          const errJson = await res.json();
          if (errJson && errJson.message) {
            message.error('创建图集失败：' + errJson.message);
            setGalleryLoading(false);
            return;
          }
        } catch (e) {}
        message.error('接口请求失败，状态码：' + res.status);
        setGalleryLoading(false);
        return;
      }

      try {
        json = await res.json();
      } catch (e) {
        message.error('接口返回格式错误');
        setGalleryLoading(false);
        return;
      }

      if (!json.success) {
        message.error('创建图集失败：' + (json.message || '未知错误'));
        setGalleryLoading(false);
        return;
      }

      const galleryUrl = json.data;

      setGenerateGalleryModalOpen(false);
      setGalleryTitle('');
      setGalleryBannerUrl('');
      setGalleryDescription('');
      setGalleryAnnouncement('');
      setGalleryLoading(false);

      // 显示成功弹框
      setCreatedGalleryUrl(galleryUrl);
      setGallerySuccessModalOpen(true);
      await copyToClipboardAsync(galleryUrl, '图集链接已复制');
  };

  // 下拉菜单项
  const menuItems = [
    {
      key: 'add',
      icon: <PlusOutlined />,
      label: '新增目录',
      onClick: () => setAddDirModalOpen(true),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '删除当前目录',
      danger: true,
      disabled: !internalDir,
      onClick: handleDeleteDir,
    },
  ];

  // 目录选择和更多操作合并为 Dropdown.Button
  const dirSelect = (
      <Dropdown.Button
        type="default"
        size="small"
        style={{ width: 'auto' }}
        loading={dirLoading}
        menu={{ items: menuItems }}
        icon={<MoreOutlined />}
        placement="bottomRight"
        onClick={e => e.preventDefault()} // 防止主按钮点击
      >
        <Select
          style={{ minWidth: 120 }}
          value={internalDir}
          onChange={dir => {
            (window as any)._dir = dir;
            setInternalDir(dir);
            onDirChange(dir);
          }}
          placeholder="选择目录"
          size="small"
          suffixIcon={<FolderOpenOutlined />}
          loading={dirLoading}
          bordered={false}
          dropdownStyle={{ minWidth: 120 }}
        >
          {dirList.map(dir => (
            <Select.Option value={dir} key={dir}>{dir}</Select.Option>
          ))}
        </Select>
      </Dropdown.Button>
  );

  // 判断选中项是否全部为图片
  const isAllImage = selectedIds.every(id => {
    const file = list.find(item => item.id === id);
    if (!file) return false;
    return isImg(file.url || '');
  });

  if (!selectedIds || selectedIds.length === 0) return (
    <div style={{margin: '16px 0', display: 'inline-flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        {dirSelect}
        <Tooltip title="关闭随机路径后有可能导致文件覆盖，或上传后链接还是旧文件。非必要不建议关闭。">
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              随机路径{randomPathEnabled ? '已开启' : '已关闭'}
            </span>
          </div>
        </Tooltip>
      <Input.Search
        placeholder="搜索文件名"
        allowClear
        enterButton={<SearchOutlined />}
        size="small"
        onChange={e => {
          onKeywordChange && onKeywordChange(e.target.value);
        }}
        onSearch={onSearch}
      />
      <Modal
        open={addDirModalOpen}
        title="新增目录"
        onCancel={() => setAddDirModalOpen(false)}
        okText="确定"
        cancelText="取消"
        onOk={async () => {
          if (!newDirName.trim()) {
            message.warning('请输入目录名');
            return;
          }
          setDirLoading(true);
          try {
            const res = await fetch('/img-dir/add', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ dirName: newDirName.trim() }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || '添加失败');
            setAddDirModalOpen(false);
            setNewDirName('');
            message.success('目录已添加');
            await fetchDirList();
          } catch (e: any) {
            message.error(e?.message || '添加失败');
          } finally {
            setDirLoading(false);
          }
        }}
        confirmLoading={dirLoading}
      >
        <Input
          value={newDirName}
          onChange={e => setNewDirName(e.target.value)}
          placeholder="请输入目录名"
          onPressEnter={async () => {
            if (!newDirName.trim()) {
              message.warning('请输入目录名');
              return;
            }
            setDirLoading(true);
            try {
              const res = await fetch('/img-dir/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dirName: newDirName.trim() }),
              });
              const json = await res.json();
              if (!json.success) throw new Error(json.message || '添加失败');
              setAddDirModalOpen(false);
              setNewDirName('');
              message.success('目录已添加');
              await fetchDirList();
            } catch (e: any) {
              message.error(e?.message || '添加失败');
            } finally {
              setDirLoading(false);
            }
          }}
        />
      </Modal>
    </div>
  );

  return (
    <>
      <div style={{margin: '16px 0', display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'flex-end'}}>
        <div style={{display: 'flex', width: '100%'}}>
        {dirSelect}
        <Tooltip title="关闭随机路径后有可能导致文件覆盖，或上传后链接还是旧文件。">
          <div style={{ display: 'flex', alignItems: 'center', marginRight: 12, marginLeft: 6 }}>
            <Switch
              checked={randomPathEnabled}
              onChange={setRandomPathEnabled}
              size="small"
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              随机路径{randomPathEnabled ? '已开启' : '已关闭'}
            </span>
          </div>
        </Tooltip>
        </div>
        <Dropdown
          menu={{
            items: [
              {
                key: 'hard-delete',
                label: '删除文件+记录',
                onClick: async () => {
                  const doDelete = window.confirm(`确定要删除所选的 ${selectedIds.length} 个文件吗？删除后文件将不可访问。`);
                  if (!doDelete) return;
                  await Promise.all(selectedIds.map(id =>
                    fetch(`/img/delete?hard=true&id=${id}&uid=${localStorage.getItem('fdd_uid')}`, { method: 'DELETE' })
                  ));
                  setSelectedIds([]);
                  loadHistory();
                  message.success('所选文件已删除');
                }
              },
            ]
          }}
        >
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={async () => {
              const doDelete = window.confirm(`确定要删除所选的 ${selectedIds.length} 个记录吗？删除后文件将不可访问。`);
              if (!doDelete) return;
              await Promise.all(selectedIds.map(id =>
                fetch(`/img/delete?id=${id}&uid=${localStorage.getItem('fdd_uid')}`, { method: 'DELETE' })
              ));
              setSelectedIds([]);
              loadHistory();
              message.success('所选文件已删除');
            }}
            style={{ marginLeft: 8 }}
          >
            删除所选
          </Button>
        </Dropdown>
        <Button
          type="primary"
          size="small"
          style={{ marginLeft: 8 }}
          disabled={!isAllImage}
          title={!isAllImage ? '只能选中图片文件才能生成图集' : ''}
          onClick={() => setGenerateGalleryModalOpen(true)} // Open modal
        >
          生成图集
        </Button>
        <Dropdown
          menu={{
            items: [
              {
                key: 'text',
                label: '文本链接',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const text = selectedFiles.map(item => item.url).join('\n');
                  await copyToClipboardAsync(text, '已复制文本链接');
                }
              },
              {
                key: 'shortlinks',
                label: '短链接',
                onClick: handleBatchCopyLinks,
                disabled: batchCopyLoading,
              },
              {
                key: 'webp',
                label: '无损压缩',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const webpUrls = selectedFiles.map(item => {
                    const [pathname, search] = item.url.split('?');
                    return search ? `${pathname}/webp?${search}` : `${pathname}/webp`;
                  }).join('\n');
                  await copyToClipboardAsync(webpUrls, '已复制无损压缩链接');
                }
              },
              {
                key: 'overseas',
                label: '海外加速链接',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const overseasUrls = selectedFiles.map(item => {
                    const urlObj = new URL(item.url);
                    return `https://xland.eu.org${urlObj.pathname}${urlObj.search}`;
                  }).join('\n');
                  await copyToClipboardAsync(overseasUrls, '已复制海外加速链接');
                }
              },
              {
                key: 'thumbnail100',
                label: '缩略图 100px',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/100x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, '已复制 100px 缩略图链接');
                }
              },
              {
                key: 'thumbnail200',
                label: '缩略图 200px',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/200x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, '已复制 200px 缩略图链接');
                }
              },
              {
                key: 'thumbnail300',
                label: '缩略图 300px',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/300x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, '已复制 300px 缩略图链接');
                }
              },
              {
                key: 'thumbnail600',
                label: '缩略图 600px',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const thumbnails = selectedFiles.map(item => {
                    const separator = item.url.includes('?') ? '&' : '?';
                    return `${item.url}${separator}imageMogr2/thumbnail/600x`;
                  }).join('\n');
                  await copyToClipboardAsync(thumbnails, '已复制 600px 缩略图链接');
                }
              },
              {
                key: 'watermark_default',
                label: '默认水印',
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt('请输入水印文字（最多10个字符）：', storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error('水印文字不能超过10个字符');
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/t/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, '已复制默认水印链接');
                  }
                }
              },
              {
                key: 'watermark_tile',
                label: '平铺水印',
                onClick: async () => {
                  const storedText = getStoredWatermarkText();
                  const customText = window.prompt('请输入水印文字（最多10个字符）：', storedText);
                  if (customText) {
                    if (customText.length > 10) {
                      message.error('水印文字不能超过10个字符');
                      return;
                    }
                    saveWatermarkText(customText);
                    const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      const text = urlSafeBase64Encode(customText);
                      return `${item.url}${separator}watermark/2/text/${text}/fontsize/20/fill/IzAwMDAwMA/fontcolor/ffffff/dissolve/10/gravity/center/batch/1/degree/30`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, '已复制平铺水印链接');
                  }
                }
              },
              {
                key: 'image_watermark',
                label: '图片水印',
                onClick: async () => {
                  const storedImageUrl = getStoredWatermarkImageUrl();
                  const watermarkImageUrl = window.prompt('请输入水印图片URL（仅支持图床小镇的图片，需要是完整的HTTP/HTTPS链接）：', storedImageUrl || 'https://z.wiki/autoupload/20230831/l32P.%E5%9B%BE%E7%89%87%E5%8A%A0%E8%BD%BD%E5%A4%B1%E8%B4%A5_%282%29.svg');
                                      if (watermarkImageUrl) {
                      // 验证URL格式
                      if (!watermarkImageUrl.startsWith('http://') && !watermarkImageUrl.startsWith('https://')) {
                        message.error('请输入完整的HTTP或HTTPS链接');
                        return;
                      }

                    saveWatermarkImageUrl(watermarkImageUrl);
                    
                    // 从水印图片URL中截取路径
                    let imageKey = '';
                    try {
                      const urlObj = new URL(watermarkImageUrl);
                      // 提取路径部分，去掉域名
                      imageKey = urlObj.pathname;
                      // 如果路径以/开头，去掉开头的/
                      if (imageKey.startsWith('/')) {
                        imageKey = imageKey.substring(1);
                      }
                    } catch (e) {
                      message.error('URL格式错误，无法解析路径');
                      return;
                    }
                    
                    const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                    const encodedImageKey = urlSafeBase64Encode(imageKey);
                    const watermarks = selectedFiles.map(item => {
                      const separator = item.url.includes('?') ? '&' : '?';
                      return `${item.url}${separator}watermark/1/image_key/${encodedImageKey}/gravity/southeast/ws/100/ht/50`;
                    }).join('\n');
                    await copyToClipboardAsync(watermarks, '已复制图片水印链接');
                  }
                }
              },
              {
                key: 'html',
                label: 'HTML',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const html = selectedFiles.map(item => `<img src="${item.url}" />`).join('\n');
                  await copyToClipboardAsync(html, '已复制 HTML');
                }
              },
              {
                key: 'bbcode',
                label: 'BBCode',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const bbcode = selectedFiles.map(item => `[img]${item.url}[/img]`).join('\n');
                  await copyToClipboardAsync(bbcode, '已复制 BBCode');
                }
              },
              {
                key: 'markdown',
                label: 'Markdown',
                onClick: async () => {
                  const selectedFiles = list.filter(item => selectedIds.includes(item.id));
                  const md = selectedFiles.map(item => `![](${item.url})`).join('\n');
                  await copyToClipboardAsync(md, '已复制 Markdown');
                }
              },
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }} loading={batchCopyLoading}>批量复制 ▼</Button>
        </Dropdown>
        <Dropdown
          trigger={["hover"]}
          menu={{
            items: [
              {
                key: 'selectAll',
                label: '全选',
                onClick: () => {
                  setSelectedIds(list.map(item => item.id));
                }
              },
              {
                key: 'invert',
                label: '反选',
                onClick: () => {
                  setSelectedIds((prev: string[]) => list.map(item => item.id).filter((id: string) => !prev.includes(id)));
                }
              },
              {
                key: 'clear',
                label: '取消选择',
                onClick: () => {
                  setSelectedIds([]);
                }
              }
            ]
          }}
        >
          <Button size="small" style={{ marginLeft: 8 }}>更多操作 ▼</Button>
        </Dropdown>
      </div>
      {/* Modal for adding new directory */}
      <Modal
        open={addDirModalOpen}
        title="新增目录"
        onCancel={() => setAddDirModalOpen(false)}
        okText="确定"
        cancelText="取消"
        onOk={async () => {
          if (!newDirName.trim()) {
            message.warning('请输入目录名');
            return;
          }
          setDirLoading(true);
          try {
            const res = await fetch('/img-dir/add', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ dirName: newDirName.trim() }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || '添加失败');
            setAddDirModalOpen(false);
            setNewDirName('');
            message.success('目录已添加');
            await fetchDirList();
          } catch (e: any) {
            message.error(e?.message || '添加失败');
          } finally {
            setDirLoading(false);
          }
        }}
        confirmLoading={dirLoading}
      >
        <Input
          value={newDirName}
          onChange={e => setNewDirName(e.target.value)}
          placeholder="请输入目录名"
          onPressEnter={async () => {
            if (!newDirName.trim()) {
              message.warning('请输入目录名');
              return;
            }
            setDirLoading(true);
            try {
              const res = await fetch('/img-dir/add', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dirName: newDirName.trim() }),
              });
              const json = await res.json();
              if (!json.success) throw new Error(json.message || '添加失败');
              setAddDirModalOpen(false);
              setNewDirName('');
              message.success('目录已添加');
              await fetchDirList();
            } catch (e: any) {
              message.error(e?.message || '添加失败');
            } finally {
              setDirLoading(false);
            }
          }}
        />
      </Modal>

      {/* Modal for generating gallery */}
      <Modal
          title="生成图集"
          open={generateGalleryModalOpen}
          onCancel={() => {
            setGenerateGalleryModalOpen(false);
            setGalleryTitle('');
            setGalleryBannerUrl('');
            setGalleryDescription('');
            setGalleryAnnouncement('');
            // 清空成功弹框状态
            setGallerySuccessModalOpen(false);
            setCreatedGalleryUrl('');
          }}
          onOk={handleGenerateGallery}
          confirmLoading={galleryLoading}
          okText="生成"
          cancelText="取消"
      >
          <div style={{ marginBottom: 16 }}>
              <label>图集标题:</label>
              <Input
                  value={galleryTitle}
                  onChange={e => setGalleryTitle(e.target.value)}
                  placeholder="请输入图集标题"
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>Banner 背景图 URL (可选):</label>
              <Input
                  value={galleryBannerUrl}
                  onChange={e => setGalleryBannerUrl(e.target.value)}
                  placeholder="请输入 Banner 背景图 URL"
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>文案介绍 (可选):</label>
              <Input.TextArea
                  value={galleryDescription}
                  onChange={e => setGalleryDescription(e.target.value)}
                  placeholder="请输入文案介绍，支持换行"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
          <div style={{ marginBottom: 16 }}>
              <label>公告 (可选):</label>
              <Input.TextArea
                  value={galleryAnnouncement}
                  onChange={e => setGalleryAnnouncement(e.target.value)}
                  placeholder="请输入公告内容，支持换行"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  onPaste={e => e.stopPropagation()}
              />
          </div>
      </Modal>

      {/* 图集创建成功弹框 */}
      <Modal
        open={gallerySuccessModalOpen}
        title="🎉 图集创建成功"
        onCancel={() => {
          setGallerySuccessModalOpen(false);
          setCreatedGalleryUrl('');
        }}
        footer={[
          <Button 
            key="copy" 
            type="primary" 
            onClick={async () => {
              await copyToClipboardAsync(createdGalleryUrl, '图集链接已复制');
            }}
          >
            复制链接
          </Button>,
                     <Button key="close" onClick={() => {
             setGallerySuccessModalOpen(false);
             setCreatedGalleryUrl('');
           }}>
             关闭
           </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'left', padding: '20px 0' }}>
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#52c41a' }}>图集链接</h4>
            <div style={{ 
              wordBreak: 'break-all', 
              userSelect: 'all', 
              fontSize: 14, 
              padding: 12, 
              background: '#f5f5f5', 
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              {createdGalleryUrl}
            </div>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px 0' }}>二维码</h4>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              padding: 16,
              background: '#fafafa',
              borderRadius: 6,
              border: '1px solid #e0e0e0'
            }}>
              <img 
                src={`https://playground.z.wiki/qrcode/generateQRCode?data=${encodeURIComponent(createdGalleryUrl)}`}
                alt="图集二维码"
                style={{ 
                  width: 200, 
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 4
                }}
                onError={(e) => {
                  // 如果二维码加载失败，显示备用文本
                  (e.target as HTMLImageElement).style.display = 'none';
                  const parent = (e.target as HTMLImageElement).parentElement;
                  if (parent && !parent.querySelector('.qr-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'qr-error';
                    errorDiv.style.cssText = 'color: #999; font-size: 14px; padding: 40px;';
                    errorDiv.textContent = '二维码加载失败';
                    parent.appendChild(errorDiv);
                  }
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              扫描二维码可直接访问图集
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!manualCopyUrl}
        title="手动复制图集链接"
        onCancel={() => setManualCopyUrl(null)}
        footer={[
          <Button key="close" onClick={() => setManualCopyUrl(null)}>
            关闭
          </Button>
        ]}
      >
        <div style={{ wordBreak: 'break-all', userSelect: 'all', fontSize: 16, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
          {manualCopyUrl}
        </div>
        <div style={{ marginTop: 12, color: '#888' }}>自动复制失败，请手动复制上方链接。</div>
      </Modal>
    </>
  );
};

export default BatchActions; 